import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import { Redirect } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import _ from 'lodash';

import { stripeApiKey } from 'config/stripe-api-key';
import actions from 'api/actions';
import StripeForm from 'components/shared/stripe-form';
import PlanCard from 'components/shared/plan-card';
import OptionChoiceCard from 'components/shared/plan-card/option-choice-card';
import Card from 'components/shared/card';
import styles from './styles.module.scss';

const stripePromise = loadStripe(stripeApiKey);

function priceInCents(priceInDollars) {
  return priceInDollars * 100;
}

const ConnectedCheckout = inject(
  'sessionStore',
  'spinnerStore',
)(
  observer(
    class Checkout extends Component {
      constructor(props) {
        super(props);

        this.state = {
          appliedDiscount: null,
          discountCodeFailure: false,
          discount_code: '',
          redirectToLogin: false,
          redirectToParentOverview: false,
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onDiscountCodeChange = this.onDiscountCodeChange.bind(this);
        this.onDiscountApply = this.onDiscountApply.bind(this);
      }

      onDiscountApply(code) {
        const { location } = this.props;
        const { discount_code } = this.state;
        const { service } = location.state;

        if (discount_code !== '') {
          actions
            .rawPost('discounts', { discount_code, plan_id: service.id })
            .then(r => this.setState({ appliedDiscount: r.data }))
            .catch(e => {
              this.setState({ discountCodeFailure: true });
              Sentry.captureException(e);
            });
        }
      }

      onDiscountCodeChange(e) {
        this.setState({ discount_code: e.target.value });
      }

      onSubmit(params) {
        const { location, sessionStore } = this.props;
        const { learningCenter, selectedOptions } = location.state;
        const { parent } = sessionStore;
        const { appliedDiscount } = this.state;
        if (parent && parent.id) {
          const servicePurchaseParams = {
            ...params,
            parent_id: parent.id,
            learning_center_id: learningCenter.id,
            selected_options: selectedOptions,
            discount_code: _.get(appliedDiscount, 'code'),
          };
          this.props.spinnerStore.on();
          actions
            .rawPost(`parents/${parent.id}/plan_purchases`, {
              plan_purchase: servicePurchaseParams,
            })
            .then(r => {
              this.props.spinnerStore.off();
              if (this.props.fetchLearningCenters)
                this.props.fetchLearningCenters();
              if (this.props.fetchParentLearningCenters)
                this.props.fetchParentLearningCenters();
              this.setState({ redirectToParentOverview: true });
            })
            .catch(e => {
              Sentry.captureException(e);
              if (this.props.fetchLearningCenters)
                this.props.fetchLearningCenters();
              if (this.props.fetchParentLearningCenters)
                this.props.fetchParentLearningCenters();
              this.props.spinnerStore.off();
              this.setState({ error: true });
            });
        } else {
          const servicePurchaseParams = {
            ...params,
            learning_center_id: learningCenter.id,
            selected_options: selectedOptions,
            discount_code: _.get(appliedDiscount, 'code'),
          };
          this.props.spinnerStore.on();
          actions
            .post('parent-plan-purchase', {
              plan_purchase: servicePurchaseParams,
            })
            .then(r => {
              if (r.data.token) {
                console.log('r.data', r.data);
                const { user, token, parent } = r.data;
                this.props.spinnerStore.off();
                this.props.sessionStore.login({ user, token, parent });
                this.props.history.push('/');
              } else {
                this.props.spinnerStore.off();
                this.setState({ redirectToLogin: true });
              }
            })
            .catch(e => {
              Sentry.captureException(e);
              this.props.spinnerStore.off();
              this.setState({ error: true });
            });
        }
      }

      render() {
        const { location, sessionStore } = this.props;
        const {
          appliedDiscount,
          discount_code,
          discountCodeFailure,
          error,
          redirectToLogin,
          redirectToParentOverview,
        } = this.state;
        const { learningCenter, service, selectedOptions } = location.state;
        console.log('service', service);
        const { parent } = sessionStore;

        const dueNow = appliedDiscount
          ? service.price - service.price * appliedDiscount.discount_percentage
          : service.price;

        return (
          <div className="container">
            {redirectToLogin && <Redirect to="/login" />}
            {redirectToParentOverview && <Redirect to="/parents/overview" />}
            <div className="row mb-4">
              <div className="col-12 pt-3">
                <h2>Order Details</h2>
              </div>
              <div className="col-12 pt-3 d-flex flex-row flex-wrap justify-content-around">
                <Card cardContainerClass={styles.learningCenterCardContainer}>
                  <img
                    src={learningCenter.image_file_name}
                    className={styles.learningCenterImage}
                    alt=""
                  />
                  <h3 className="mt-3">{learningCenter.title}</h3>
                </Card>
                <PlanCard plan={service} />
                {_.keys(selectedOptions).length > 0 && (
                  <div className="pt-3 pt-md-0">
                    <h4>Selections</h4>
                    <div className="pt-3 d-flex flex-row flex-wrap">
                      {_.toPairs(selectedOptions).map(
                        ([optionId, choiceId]) => (
                          <div key={optionId}>
                            <h5>
                              {_.find(service.options, ['id', optionId]).name}
                            </h5>
                            <OptionChoiceCard
                              choice={_.find(
                                _.find(service.options, ['id', optionId])
                                  .choices,
                                ['id', choiceId],
                              )}
                            />
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.whiteBackground}>
              <div className="row mb-4">
                <div className="col-12">
                  <h3>
                    Due Now:&nbsp;
                    {appliedDiscount ? (
                      <React.Fragment>
                        <s className="text-gray faded">${service.price}</s>
                        &nbsp;
                        <span className="text-green">
                          $
                          {service.price -
                            service.price * appliedDiscount.discount_percentage}
                        </span>
                      </React.Fragment>
                    ) : (
                      <span>${service.price}</span>
                    )}
                  </h3>
                  {appliedDiscount ? (
                    <p>Discount Applied: {appliedDiscount.code}</p>
                  ) : (
                    <div className="form-group flex-column d-flex ">
                      <label htmlFor="discount_code">Discount Code</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          style={{ height: 'initial' }}
                          name="discount_code"
                          placeholder="Enter Discount Code"
                          value={discount_code}
                          onChange={this.onDiscountCodeChange}
                          onBlur={this.onDiscountApply}
                        />
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="button apple"
                            onClick={this.onDiscountApply}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                      {discountCodeFailure && (
                        <p className="mt-3 text-red font-heavy text-center mb-0">
                          Invalid Discount Code
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {priceInCents(dueNow) > 0 ? (
                <Elements
                  stripe={stripePromise}
                  options={{
                    mode: 'payment',
                    currency: 'usd',
                    amount: priceInCents(dueNow),
                  }}
                >
                  <ElementsConsumer>
                    {({ stripe, elements }) => (
                      <StripeForm
                        stripe={stripe}
                        elements={elements}
                        onComplete={this.onSubmit}
                        planId={service.id}
                        parent={parent}
                        disablePaymentElements={
                          appliedDiscount &&
                            service.price -
                            service.price *
                            appliedDiscount.discount_percentage ===
                            0
                        }
                        appliedDiscount={appliedDiscount}
                        priceInCents={priceInCents(dueNow)}
                      />
                    )}
                  </ElementsConsumer>
                </Elements>
              ) : (
                <StripeForm
                  onComplete={this.onSubmit}
                  planId={service.id}
                  parent={parent}
                  disablePaymentElements={
                    appliedDiscount &&
                      service.price -
                      service.price *
                      appliedDiscount.discount_percentage ===
                      0
                  }
                  appliedDiscount={appliedDiscount}
                  priceInCents={priceInCents(dueNow)}
                />
              )}
            </div>
          </div>
        );
      }
    },
  ),
);

export default ConnectedCheckout;
